import { FC, useContext, useEffect, useState } from "react";
import {
  Anchor,
  Badge,
  Box,
  Flex,
  Image,
  Paper,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { getPresentationParamsForOrigin } from "../../../../getPresentationParamsForOrigin";
import { Link } from "react-router-dom";
import { TeamLogo } from "../../../../Components/TeamLogo";
import { TeamName } from "../../../../Components/TeamName";
import { CompetitionContext } from "../../../../CompetitionContext";
import { useDebouncedCurrentGame } from "../../../../hooks/useDebouncedCurrentGame";
import { useUpcomingGames } from "../../../../hooks/useUpcomingGames";
import { useCompetition } from "../../../../hooks/useCompetition";
import { DateTime } from "luxon";
import { IconLivePhoto } from "@tabler/icons-react";

export const MatchCenterHero: FC = () => {
  const competitionId = useContext(CompetitionContext);
  const theme = useMantineTheme();

  const currentGame = useDebouncedCurrentGame(competitionId, 5000);
  const upcomingGames = useUpcomingGames(competitionId, true);
  const competition = useCompetition(competitionId);
  const nextGame = upcomingGames?.[0];

  // Create matchday name lookup
  const getMatchdayName = (matchdayId?: string): string | undefined => {
    if (!matchdayId || !competition.isSuccess) return undefined;
    return competition.data.matchdays.find((m) => m.id === matchdayId)?.name;
  };

  // Check if current game is live (started and not finished)
  const isLive =
    currentGame.isSuccess &&
    currentGame.data.started &&
    !currentGame.data.result;

  // Use live game if available, otherwise use next upcoming game
  const displayGame = isLive ? currentGame.data : nextGame;

  // Countdown state
  const [countdown, setCountdown] = useState<string>("");

  useEffect(() => {
    if (!displayGame || isLive) {
      setCountdown("");
      return;
    }

    const updateCountdown = () => {
      const now = DateTime.now();
      const gameTime = DateTime.fromISO(displayGame.dateTime);
      const diff = gameTime.diff(now, ["days", "hours", "minutes", "seconds"]);

      if (diff.as("seconds") <= 120) {
        setCountdown("Gleich");
        return;
      }

      const totalHours = diff.as("hours");
      const totalDays = diff.as("days");

      if (totalHours >= 24) {
        const days = Math.round(totalDays);
        setCountdown(days === 1 ? "1 Tag" : `${String(days)} Tage`);
      } else if (totalHours >= 1) {
        const hours = Math.round(totalHours);
        if (hours >= 24) {
          setCountdown("1 Tag");
        } else {
          setCountdown(hours === 1 ? "1 Stunde" : `${String(hours)} Stunden`);
        }
      } else {
        const minutes = Math.ceil(diff.as("minutes"));
        setCountdown(`${String(minutes)} Min.`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [displayGame, isLive]);

  if (!displayGame) {
    return null;
  }

  const { homeTeamId, guestTeamId } = displayGame.actualTeams;

  if (!homeTeamId || !guestTeamId) {
    return null;
  }

  const gameDateTime = DateTime.fromISO(displayGame.dateTime);
  const presentator = getPresentationParamsForOrigin().ads?.presentation;

  return (
    <Stack gap="sm">
      <Paper
        radius="lg"
        p={0}
        style={{
          background: `linear-gradient(135deg, ${theme.colors.fwcBlue[9]} 0%, ${theme.colors.fwcBlue[7]} 100%)`,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Header */}
        <Flex
          justify="space-between"
          align="center"
          px="md"
          py="xs"
          style={{
            borderBottom: `1px solid rgba(255,255,255,0.1)`,
          }}
        >
          <Text size="xs" c="white" opacity={0.8} tt="uppercase" fw={600}>
            {isLive ? "Live" : "Nächstes Spiel"}
          </Text>
          {isLive ? (
            <Badge
              color="red"
              variant="filled"
              size="sm"
              leftSection={<IconLivePhoto size={12} />}
              style={{ animation: "pulse 2s infinite" }}
            >
              LIVE
            </Badge>
          ) : (
            <Text size="xs" c="white" opacity={0.8}>
              {gameDateTime.toFormat("ccc., dd.MM. - HH:mm")} Uhr
            </Text>
          )}
        </Flex>

        {/* Match Display */}
        <Flex align="flex-start" justify="center" py="xl" px="md" gap="md">
          {/* Home Team */}
          <Anchor
            component={Link}
            to={`/teams/${homeTeamId}`}
            underline="never"
            style={{ flex: 1 }}
          >
            <Flex direction="column" align="center" gap="sm">
              <Flex
                align="center"
                justify="center"
                style={{
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "50%",
                  width: 84,
                  height: 84,
                }}
              >
                <Box w={56}>
                  <TeamLogo teamId={homeTeamId} height={56} />
                </Box>
              </Flex>
              <Text
                c="white"
                fw={600}
                ta="center"
                size="sm"
                style={{ lineHeight: 1.2 }}
              >
                <TeamName teamId={homeTeamId} variant="medium" />
              </Text>
            </Flex>
          </Anchor>

          {/* Score / VS */}
          <Flex direction="column" align="center" gap="xs" mx="md" pt={20}>
            {isLive || displayGame.started ? (
              <Text
                c="white"
                fw={700}
                style={{
                  fontSize: 48,
                  fontFamily: "Chivo Mono",
                  lineHeight: 1,
                }}
              >
                {displayGame.score.home}:{displayGame.score.guest}
              </Text>
            ) : (
              <Text
                c="white"
                fw={700}
                opacity={0.5}
                style={{
                  fontSize: 32,
                  fontFamily: "Chivo",
                  lineHeight: 1,
                }}
              >
                VS
              </Text>
            )}
            {countdown && !isLive && (
              <Badge color="rgba(255,255,255,0.2)" size="lg" radius="sm">
                <Text c="white" fw={600} size="xs">
                  {countdown}
                </Text>
              </Badge>
            )}
            {isLive && (
              <Text c="white" opacity={0.7} size="xs" ta="center">
                {getMatchdayName(displayGame.matchdayId) ?? displayGame.name}
              </Text>
            )}
          </Flex>

          {/* Guest Team */}
          <Anchor
            component={Link}
            to={`/teams/${guestTeamId}`}
            underline="never"
            style={{ flex: 1 }}
          >
            <Flex direction="column" align="center" gap="sm">
              <Flex
                align="center"
                justify="center"
                style={{
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "50%",
                  width: 84,
                  height: 84,
                }}
              >
                <Box w={56}>
                  <TeamLogo teamId={guestTeamId} height={56} />
                </Box>
              </Flex>
              <Text
                c="white"
                fw={600}
                ta="center"
                size="sm"
                style={{ lineHeight: 1.2 }}
              >
                <TeamName teamId={guestTeamId} variant="medium" />
              </Text>
            </Flex>
          </Anchor>
        </Flex>

        {/* CSS Animation for pulse */}
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.6; }
            }
          `}
        </style>
      </Paper>

      {presentator && (
        <Paper p="sm" radius="md" withBorder>
          <Stack gap="xs" align="center">
            <Text size="xs" c="dimmed">
              App präsentiert von
            </Text>
            <Anchor
              href={presentator.linkUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Image
                alt={presentator.name}
                src={presentator.logoUrl}
                h={56}
                w="auto"
                fit="contain"
              />
            </Anchor>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};
